import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { from, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { CompanyTaxTypeApiService } from '../../empresas/services/company-tax-type-api.service';
import { CompanyTaxType } from '../../empresas/models/empresa.model';
import { CurrencyService } from '../../currency/currency.service';
import { CountryResponse } from '../../currency/models/currency.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';

/**
 * Tipos de documento (identificación fiscal) por defecto para sembrar rápido en un negocio nuevo.
 * El regex va con UN backslash literal (`\.`); en el string TS se escribe `\\.`.
 * `countryAliases` permite resolver el countryId por nombre del país; si no se encuentra
 * en el catálogo cargado, se usa `fallbackCountryId` (ids 1..8 del baseline).
 */
interface DefaultTaxType {
  name: string;
  formatRegex: string;
  countryAliases: string[];
  fallbackCountryId: number;
}

const DEFAULT_TAX_TYPES: DefaultTaxType[] = [
  { name: 'EIN', formatRegex: '^[0-9]{2}-[0-9]{7}$', countryAliases: ['United States', 'USA', 'Estados Unidos', 'EE.UU.', 'Estados Unidos de America'], fallbackCountryId: 1 },
  { name: 'RUC', formatRegex: '^[0-9]{11}$', countryAliases: ['Peru'], fallbackCountryId: 2 },
  { name: 'RFC', formatRegex: '^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$', countryAliases: ['Mexico'], fallbackCountryId: 3 },
  { name: 'NIT', formatRegex: '^[0-9]{9,10}$', countryAliases: ['Colombia'], fallbackCountryId: 4 },
  { name: 'CUIT', formatRegex: '^[0-9]{2}-[0-9]{8}-[0-9]{1}$', countryAliases: ['Argentina'], fallbackCountryId: 5 },
  { name: 'RUT', formatRegex: '^[0-9]{1,2}\\.[0-9]{3}\\.[0-9]{3}-[0-9Kk]{1}$', countryAliases: ['Chile'], fallbackCountryId: 6 },
  { name: 'CNPJ', formatRegex: '^[0-9]{2}\\.[0-9]{3}\\.[0-9]{3}/[0-9]{4}-[0-9]{2}$', countryAliases: ['Brazil', 'Brasil'], fallbackCountryId: 7 },
  { name: 'CIF/NIF', formatRegex: '^[A-Z][0-9]{7}[A-Z0-9]$', countryAliases: ['Spain', 'España'], fallbackCountryId: 8 },
];

@Component({
  selector: 'app-company-tax-type-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    HasPermissionDirective,
  ],
  templateUrl: './company-tax-type-list.component.html',
})
export class CompanyTaxTypeListComponent implements OnInit {
  items: CompanyTaxType[] = [];
  loading = false;
  seeding = false;
  error = '';

  /** countryId -> nombre del país, para mostrar el país por nombre (no por id). */
  private countryNames = new Map<number, string>();
  /** nombre normalizado del país -> id, para resolver los seeds por nombre. */
  private countryIdByName = new Map<string, number>();

  constructor(
    private api: CompanyTaxTypeApiService,
    private currencyService: CurrencyService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarPaises();
    this.cargar();
  }

  private cargarPaises(): void {
    this.currencyService.getCountries().subscribe({
      next: (countries) => {
        for (const c of countries) {
          this.countryNames.set(c.id, c.name);
          this.countryIdByName.set(this.normalize(c.name), c.id);
        }
      },
      // Silencioso: si falla, igual se listan los tipos (sin nombre de país).
      error: () => {},
    });
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api.getAll({ page: 0, size: 200 }).subscribe({
      next: (page) => {
        this.items = page.content ?? [];
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar los tipos de documento';
        this.loading = false;
      },
    });
  }

  countryName(item: CompanyTaxType): string {
    if (item.countryId == null) return '';
    return this.countryNames.get(item.countryId) ?? '';
  }

  nuevo(): void {
    this.router.navigate(['/config-empresas/tipos-documento/crear']);
  }

  editar(item: CompanyTaxType): void {
    this.router.navigate(['/config-empresas/tipos-documento/editar', item.id]);
  }

  async eliminar(item: CompanyTaxType): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar tipo de documento',
      message: `¿Está seguro de eliminar el tipo de documento "${item.name}"?`,
    });
    if (!ok) return;
    this.api.delete(item.id).subscribe({
      next: () => {
        this.cargar();
        this.toast.deleted('Tipo de documento', `Se eliminó "${item.name}" correctamente.`);
      },
      error: (err) => {
        const msg = err.status === 403
          ? 'Necesitas rol Admin para eliminar tipos de documento'
          : err.error?.message || 'Error al eliminar el tipo de documento';
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  /** Crea los 8 tipos estándar uno por uno; 409 = ya existe (omitido). */
  cargarPorDefecto(): void {
    if (this.seeding) return;
    this.seeding = true;

    const cuerpos: Omit<CompanyTaxType, 'id'>[] = DEFAULT_TAX_TYPES.map((d) => ({
      name: d.name,
      formatRegex: d.formatRegex,
      countryId: this.resolveCountryId(d),
    }));

    let creados = 0;
    let omitidos = 0;
    let prohibidos = 0;
    let fallidos = 0;

    from(cuerpos)
      .pipe(
        concatMap((body) =>
          this.api.create(body).pipe(
            map(() => 'created' as const),
            catchError((err) => {
              if (err.status === 409) return of('skipped' as const);
              if (err.status === 403) return of('forbidden' as const);
              return of('failed' as const);
            })
          )
        )
      )
      .subscribe({
        next: (r) => {
          if (r === 'created') creados++;
          else if (r === 'skipped') omitidos++;
          else if (r === 'forbidden') prohibidos++;
          else fallidos++;
        },
        complete: () => {
          this.seeding = false;
          if (prohibidos > 0 && creados === 0 && omitidos === 0) {
            this.toast.error({
              title: 'Sin permiso',
              message: 'Necesitas rol Manager para crear tipos de documento',
            });
          } else {
            const partes = [`${creados} creados`, `${omitidos} ya existían`];
            if (fallidos > 0) partes.push(`${fallidos} con error`);
            this.toast.success(partes.join(', '));
          }
          this.cargar();
        },
      });
  }

  private resolveCountryId(d: DefaultTaxType): number {
    for (const alias of d.countryAliases) {
      const id = this.countryIdByName.get(this.normalize(alias));
      if (id != null) return id;
    }
    return d.fallbackCountryId;
  }

  private normalize(s: string): string {
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  }
}
